import type { FeatureFlag } from '@forgd/shared/feature-flags'
import type { Component } from 'vue'
import { ForgdToolsIcon } from '@forgd/ui-kit/icons'
import { InformationCircleIcon, PresentationChartBarIcon } from '@heroicons/vue/24/outline'

export interface DrawerTile {
  label: string
  description: string
  icon: string
  to?: string
  external?: boolean
  badge?: string
  featureFlag?: FeatureFlag
}

export interface NavAccessContext {
  featureFlags: string[]
}

export function filterByFeatureAccess<T extends Pick<DrawerTile, 'featureFlag'>>(items: T[], ctx: NavAccessContext): T[] {
  return items.filter(item => !item.featureFlag || ctx.featureFlags.includes(item.featureFlag))
}

export interface DrawerTextLink {
  label: string
  to: string
  external?: boolean
}

export interface HomeNavDrawer {
  title: string
  icon: Component
  badge?: string
  tiles: DrawerTile[]
  tilesHeading?: string
  links?: DrawerTextLink[]
  socials?: boolean
}

function sectionRoot(to: string): string {
  const [segment] = to.split('/').filter(Boolean)
  return segment ? `/${segment}` : to
}

export function drawerNavTargets(drawer: HomeNavDrawer): Array<{ to: string }> {
  return [...drawer.tiles, ...(drawer.links ?? [])]
    .filter(item => item.to && !item.external)
    .map(item => ({ to: sectionRoot(item.to!) }))
}

export function buildHomeNavDrawers() {
  return {
    'home-tools': {
      title: 'Tools',
      icon: ForgdToolsIcon,
      badge: 'Free',
      tiles: [
        { label: 'Design Tokenomics', description: 'Model emissions, vesting and demand. Publish a one-pager', icon: '/icons/design-tokenomics.svg', to: '/design-tokenomics' },
        { label: 'Engage Market Makers', description: 'Conduct a competitive RFQ with 40 integrated market makers', icon: '/icons/engage-a-market-maker.svg', to: '/engage-a-market-maker/request-for-quotations', badge: 'Recommended' },
        { label: 'List on Exchanges', description: 'Automate exchange listing, assess fees and liquidity requirements', icon: '/icons/exchange-listing-research.svg', to: '/list-on-exchanges' },
        { label: 'Monitor Market Makers', description: 'Track depth, volume, spreads, and KPI adherence', icon: '/icons/market-maker-monitoring-next.svg', to: '/market-maker-monitoring' },
        { label: 'Track Token Unlocks', description: 'Track upcoming emissions, and optimize for price', icon: '/icons/token-unlock-monitoring.svg', to: '/token-unlocks' },
        { label: 'Analyze Trade Activity', description: 'See the performance of projects across all exchanges', icon: '/icons/financial-analysis.svg', to: '/analyze-trade-activity' },
      ],
    },
    'home-research': {
      title: 'Research & Insights',
      icon: PresentationChartBarIcon,
      badge: 'Free',
      tiles: [
        { label: 'Tokenomics Analysis', description: 'Analyze distribution, emissions, float, and performance trends', icon: '/icons/tokenomics-analysis.svg', to: '/research/tokenomics' },
        { label: 'Exchange Listing Trends', description: 'Track listing performance, fees, and diligence criteria', icon: '/icons/exchange-listing-trends.svg', to: '/research/exchange-and-listing' },
        { label: 'Market Maker Leaderboards', description: 'Compare market makers across 500+ tracked engagements', icon: '/icons/market-maker-leaderboards.svg', to: '/market-maker-leaderboard' },
        { label: 'Universal Token Rating', description: 'Rank projects by disclosure quality and performance', icon: '/icons/tokenomics-and-launch-research.svg', to: '/universal-token-rating', badge: 'Beta' },
        { label: 'Reports', description: 'Access ad hoc research from the Forgd team', icon: '/icons/reports.svg', badge: 'Coming Soon' },
      ],
    },
    'home-about': {
      title: 'About Forgd',
      icon: InformationCircleIcon,
      tilesHeading: 'Use Cases',
      tiles: [
        { label: 'Blockchain Projects', description: 'End-to-end free tools, and advisory for pre-TGE & listed blockchain projects.', icon: '/icons/about-blockchain-projects.svg', to: '/about/blockchain-projects' },
        { label: 'Market Makers', description: 'Participate in RFQs, monitor engagements, and rank in the Market Maker leaderboard.', icon: '/icons/about-market-makers.svg', to: '/about/market-makers' },
        { label: 'Web3 Advisory Firms', description: 'Leverage your engagements and streamline your operations with Forgd. Free to use.', icon: '/icons/about-exchanges.svg', to: '/about/blockchain-advisors' },
      ],
      links: [
        { label: 'What is Forgd?', to: '/about/what-is-forgd' },
        { label: 'Forgd Advisory', to: '/about/advisory' },
        { label: 'Pricing', to: '/about/pricing' },
        { label: 'Press & Blog', to: '/blog', external: true },
        { label: 'FAQs', to: '/about/faqs' },
        { label: 'Contact & Support', to: '/about/contact' },
      ],
      socials: true,
    },
  } satisfies Record<string, HomeNavDrawer>
}
